import { itemHeaderMessageActions } from './ItemActions.locstring.json';
import addPinnedOnlyActionToOverflow from '../utils/addPinnedOnlyActionToOverflow';
import createStaticActionCommandBarItem from '../utils/createStaticActionCommandBarItem';
import getConversationId from '../utils/getConversationId';
import getHandlerAndLogAction from '../utils/getHandlerAndLogAction';
import getReadStaticActionsMap from '../utils/getReadStaticActionsMap';
import isGroupItem from '../utils/isGroupItem';
import { menuKeysInOrder } from '../utils/menuKeysInOrder';
import removePinnedOnlyActionFromOverflow from '../utils/removePinnedOnlyActionFromOverflow';
import { AddinsInitializedState } from '../utils/schema/AddinsInitializedState';
import { ItemActionsProps } from '../utils/schema/ItemActionsProps';
import updateSelectedItemPart from '../utils/updateSelectedItemPart';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { observer as observerLite } from 'mobx-react-lite';
import { CommandBarButton, IButtonProps } from 'office-ui-fabric-react/lib/Button';
import { CommandBar, ICommandBar } from 'office-ui-fabric-react/lib/CommandBar';
import { DirectionalHint } from 'office-ui-fabric-react/lib/ContextualMenu';
import { TooltipHost } from 'office-ui-fabric-react/lib/Tooltip';
import { getReadHostItemIndex, shouldAddinsActivate } from 'owa-addins-core';
import { ExtensibilityModeEnum } from 'owa-addins-types';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc, { isStringNullOrWhiteSpace } from 'owa-localize';
import { mailEllipsisAriaLabelText } from 'owa-locstrings/lib/strings/mailellipsisarialabeltext.locstring.json';
import { MoreActionsButton } from 'owa-locstrings/lib/strings/moreactionsbutton.locstring.json';
import { lazyCreateMailReadAdapter } from 'owa-mail-addins';
import findInlineComposeViewState from 'owa-mail-compose-actions/lib/utils/findInlineComposeViewState';
import { initializeLikeUnlikeTracker } from 'owa-mail-reading-pane-store/lib/actions/itemActions/initializeLikeUnlikeTracker';
import ItemPartViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemPartViewState';
import { getFocusedItemPart } from 'owa-mail-reading-pane-store/lib/utils/focusedItemPartUtils';
import { ReadActionKey } from 'owa-outlook-service-options';
import { lazySubscribeToResizeEvent, lazyUnsubscribeFromResizeEvent } from 'owa-resize-event';
import doesFolderIdEqualName from 'owa-session-store/lib/utils/doesFolderIdEqualName';
import doesMessageHaveSmimePrefix from 'owa-smime/lib/utils/doesMessageHaveSmimePrefix';
import { ReadStaticAction } from 'owa-surface-actions';
import * as React from 'react';
import populateCommandBarWithAddins, {
    tryAddDivider,
} from 'owa-addins-surface-actions/lib/utils/populateCommandBarWithAddins';
import {
    getOptionsForFeature,
    OwsOptionsFeatureType,
    SurfaceActionsOptions,
} from 'owa-outlook-service-options';
import {
    lazyInitializeAddinsForItem,
    lazyDeinitializeAddinsForItem,
    MessageReadAdapter,
} from 'owa-addins-core';
import {
    shouldAddActionToEnd,
    shouldActionBeOnReadingPaneSurface,
    isReadingPaneSurfaceActionPinnedByUser,
    SurfaceActionItem,
    onCommandBarDataReduced,
    onCommandBarDataGrown,
} from 'owa-surface-actions';
const styles = require('./ItemActions.scss');
const classNames = require('classnames/bind').bind(styles);

const timeoutToInitializeAddins = 1;

interface ItemHeaderCommandBarData {
    items: SurfaceActionItem[];
    overflowItems: SurfaceActionItem[];
}

interface ItemActionsState {
    addinsInitializedState: AddinsInitializedState;
}

@observer
export default class ItemActionsMenu extends React.Component<ItemActionsProps, ItemActionsState> {
    private commandBarHiddenOverflowItems: SurfaceActionItem[] = [];
    private commandBarDividerKey: number = 0;
    private commandBar: ICommandBar;
    // Actions that were pinned based on some application logic are tracked here.
    //
    // This is tracked separately from the data dependencies of actions
    // being present in the list, because we want the list of actions to be
    // consistent on an item over its lifetime, even if those data
    // dependencies change
    //
    // e.g. if there are likes on a message, then all users unlike it,
    // we want the like SurfaceAction to still be present with a like count
    // of 0
    //
    // This is tracked on the component because we want its lifetime to be tied to the
    // component's lifetime -- e.g. if we unmount the component & remount it (by expanding
    // and collapsing the item, or by switching tabs and switching back), we want to reset
    // the items to be based strictly on the data model.
    private forcePinnedSurfaceActions: Set<ReadActionKey> = new Set();

    private intializeAddinsTask: number;

    constructor(props: ItemActionsProps) {
        super(props);
        this.state = { addinsInitializedState: AddinsInitializedState.NotStarted };
        initializeLikeUnlikeTracker(this.props.viewState);
    }

    private createCommandBarItems(): ItemHeaderCommandBarData {
        // Actions will be rendered both on the surface (when pinned) *AND* in the overflow.
        // To support this behavior, we override the reduce/grow logic of the commandBar and push items to our own local overflow collection.
        // This collection will not be read by the commandBar, but we need to clear it each time we construct the commandBarData.
        this.commandBarHiddenOverflowItems = [];

        const surfaceItems: SurfaceActionItem[] = [];
        const overflowItems: SurfaceActionItem[] = [];

        const readStaticActionsMap = getReadStaticActionsMap();
        const readStaticActions = [];
        // This can be a customized list like in case of OOFRollUpExpanded or smime-prefixed messages
        // or the complete list of actions with their submenus.
        const keysList =
            this.props.customizedSubsetActionKeys?.length > 0
                ? this.props.customizedSubsetActionKeys
                : menuKeysInOrder;

        keysList.forEach(key => {
            // Check if the action corresponding to this key exists in the map.
            // For eg., AddToProjectSpace will exist in readStaticActionsMap only if
            // 'outlookSpaces-enabled' feature is enabled. [see getReadStaticActions in getStaticActions.ts]
            if (readStaticActionsMap[key]) {
                readStaticActions.push(readStaticActionsMap[key]);
            }
        });

        readStaticActions.forEach(action => {
            let commandBarItem: SurfaceActionItem = null;
            if (action.key === 'Addins') {
                this.tryCreateAddinsCommandBarItems(surfaceItems, overflowItems, action);
                return;
            }

            // Get the commandBarItem from external map.
            commandBarItem = createStaticActionCommandBarItem(action, this.props);

            if (commandBarItem) {
                this.addItemToCommandBar(commandBarItem, overflowItems, surfaceItems);
            }
        });

        return {
            items: !this.props.hideSurfaceButton ? surfaceItems : [],
            overflowItems: overflowItems,
        };
    }

    private addItemToCommandBar(
        commandBarItem: SurfaceActionItem,
        overflowItems: SurfaceActionItem[],
        surfaceItems: SurfaceActionItem[]
    ) {
        let commandBarItemClone = commandBarItem;
        if (this.shouldAddItemToSurface(commandBarItem)) {
            this.addItemToCommandBarSurface(surfaceItems, commandBarItem);
            commandBarItemClone = { ...commandBarItem };
        }

        // Add clone of submenu items to command bar surface if they satisfy the required conditions.
        // We add clone to be able to handle things like onRender differently for surface items and overflow items.
        commandBarItem.subMenuProps?.items?.forEach(subMenuItem => {
            this.shouldAddItemToSurface(subMenuItem) &&
                this.addItemToCommandBarSurface(surfaceItems, { ...subMenuItem });
        });

        // commandBarItemClone will either be a copy of the original item,
        // or the original item if it didn't get added to the surface.
        this.addItemToCommandBarOverflow(overflowItems, commandBarItemClone);
    }

    private addItemToCommandBarSurface(
        surfaceItems: SurfaceActionItem[],
        item: SurfaceActionItem
    ): void {
        const readActionKey = item.key as ReadActionKey;

        // Track if this item was forced to the item surface by
        // some item state.
        if (!isReadingPaneSurfaceActionPinnedByUser(readActionKey)) {
            this.forcePinnedSurfaceActions.add(readActionKey);
        }

        item.isPinned = true; // Mark the item as pinned for telemetry.
        if (shouldAddActionToEnd(readActionKey)) {
            surfaceItems.push(item);
        } else {
            surfaceItems.unshift(item);
        }
    }

    private addItemToCommandBarOverflow(
        overflowItems: SurfaceActionItem[],
        item: SurfaceActionItem
    ): void {
        if (!item.isPinnedOnly) {
            const key = item.key as ReadActionKey;
            // Remove all custom rendering for overflow menu and submenus
            item.onRender = null;
            if (key !== 'AddToProjectSpace') {
                item.subMenuProps?.items?.forEach(subMenuItem => {
                    subMenuItem.onRender = null;
                });
            }

            // Mark the item and its submenu as renderedInOverflow for telemetry
            item.renderedInOverflow = true;
            item.subMenuProps?.items?.forEach(subMenuItem => {
                subMenuItem.renderedInOverflow = true;
            });
            if (key == '-') {
                tryAddDivider(overflowItems, this.commandBarDividerKey++);
            } else {
                overflowItems.push(item);
            }
        }
    }

    private shouldAddItemToSurface(item: SurfaceActionItem): boolean {
        // If the setting is hide surface button, do not add to surface
        if (this.props.hideSurfaceButton) {
            return false;
        }

        const readActionKey = item.key as ReadActionKey;
        // Add the item to the surface if it is pinned or if it was _previously_ *forced*
        // to the surface on this item.
        return (
            shouldActionBeOnReadingPaneSurface(readActionKey, this.props.item) ||
            this.forcePinnedSurfaceActions.has(readActionKey)
        );
    }

    private onMenuClickCallback = () => {
        updateSelectedItemPart(this.props.item, this.props.viewState);
    };

    componentDidMount() {
        lazySubscribeToResizeEvent.importAndExecute(this.onResize);
        this.initailizeAddinsIfNeeded();
    }

    componentWillUnmount() {
        lazyUnsubscribeFromResizeEvent.importAndExecute(this.onResize);
        this.deInitializeAddins();
    }

    componentDidUpdate() {
        this.initailizeAddinsIfNeeded();
    }

    render() {
        const isNotInNotesFolder = this.props.item.ParentFolderId
            ? !doesFolderIdEqualName(this.props.item.ParentFolderId.Id, 'notes')
            : true;

        return (
            <div className={styles.surfaceActionsCommandBarContainer}>
                {isNotInNotesFolder && this.renderCommandBar()}
            </div>
        );
    }

    private renderCommandBar(): JSX.Element {
        if (!this.props.viewState) {
            return null;
        }
        const commandBarData = this.createCommandBarItems();

        return (
            <CommandBar
                componentRef={this.getRef}
                items={commandBarData.items}
                overflowItems={commandBarData.overflowItems}
                overflowButtonProps={{
                    iconProps: {
                        iconName: ControlIcons.More,
                    },
                    menuIconProps: {
                        iconName: ControlIcons.More,
                        styles: {
                            root: isStringNullOrWhiteSpace(this.props.seeMoreStyles)
                                ? styles.surfaceActionsCommandBarOverflowIcon
                                : classNames(
                                      styles.surfaceActionsCommandBarOverflowIcon,
                                      this.props.seeMoreStyles
                                  ),
                        },
                    },
                    ariaLabel: loc(mailEllipsisAriaLabelText),
                }}
                buttonAs={this.ItemHeaderToolbarButton}
                overflowButtonAs={this.ItemHeaderToolbarOverflowButton}
                shiftOnReduce={true}
                onDataReduced={onCommandBarDataReduced}
                onDataGrown={onCommandBarDataGrown}
                styles={{
                    root: styles.surfaceActionsCommandBar,
                    primarySet: styles.surfaceActionsCommandBarPrimarySet,
                }}
                ariaLabel={loc(itemHeaderMessageActions)}
                onReduceData={this.onCommandBarReduceData}
                onGrowData={this.onCommandBarGrowData}
            />
        );
    }

    private getRef = (ref: ICommandBar) => {
        this.commandBar = ref;
    };

    // Until https://github.com/OfficeDev/office-ui-fabric-react/issues/5300 is fixed, we need to intercept props.menuProps and add
    // directionalHint, onMenuOpened, and onMenuDismissed here if the menuProps exist.
    private ItemHeaderToolbarButton = observerLite((props: IButtonProps) => (
        <CommandBarButton
            {...props}
            menuProps={
                props.menuProps && {
                    ...props.menuProps,
                    directionalHint: DirectionalHint.bottomAutoEdge,
                }
            }
            onMenuClick={
                // Some add-in surface buttons are menu buttons.
                // In Office UI Fabric, a menu button's default onClick/onKeyDown behavior is open contexual menu,
                // to add extra common handling, we need to provide onMenuClick prop here.
                this.onMenuClickCallback
            }
            styles={{
                ...props.styles,
                root: classNames(styles.surfaceActionsCommandBarButton, styles.iconButton),
                rootChecked: styles.checkedCommandBarButton,
                rootDisabled: styles.disabledReplyActionButton,
                icon: styles.enabledIconButton,
                iconDisabled: styles.disabledIconButton,
                menuIcon: styles.commandBarMenuIcon,
            }}
            role={'button'}
        />
    ));

    private ItemHeaderToolbarOverflowButton = observerLite((props: IButtonProps) => {
        const ToolbarButton: (props: IButtonProps) => JSX.Element = this.ItemHeaderToolbarButton;
        return (
            <TooltipHost content={loc(MoreActionsButton)}>
                <ToolbarButton {...props} />
            </TooltipHost>
        );
    });

    private onCommandBarReduceData = (data: any): any => {
        // Logic copied from fabric's commandBar with some changes.
        // Called on a resize when the commandBar attempts to remove an item from the surface.
        // VSO #31334 data type is currently 'any' as fabric doesn't yet export the proper ICommandBarData type.
        let { primaryItems, overflowItems, cacheKey } = data;

        // Get the first item in the primary (surface) collection.
        const movedItem = primaryItems[0];

        if (movedItem !== undefined) {
            movedItem.isPinnedOnly && addPinnedOnlyActionToOverflow(movedItem, overflowItems);
            // If it exists, add it to the start of our local collection of hidden overflow items,
            // remove it from the primary (surface) collection, compute the cacheKey, and return the new data.
            this.commandBarHiddenOverflowItems = [movedItem, ...this.commandBarHiddenOverflowItems];
            primaryItems = primaryItems.slice(1);
            cacheKey = this.computeCommandBarCacheKey(primaryItems);

            return { ...data, primaryItems, overflowItems, cacheKey };
        }

        // If we have no more primary (surface) items to reduce, return undefined so the commandBar's resize logic will stop.
        return undefined;
    };

    private onCommandBarGrowData = (data: any): any => {
        // Logic copied from fabric's command bar with some changes.
        // Called on a resize when the commandBar attempts to add an item back to the surface.
        // VSO #31334 data type is currently 'any' as fabric doesn't yet export the proper ICommandBarData type.
        let { primaryItems, overflowItems, cacheKey } = data;

        // Get the first item from our local collection of hidden overflow items.
        const movedItem = this.commandBarHiddenOverflowItems[0];

        if (movedItem !== undefined) {
            movedItem.isPinnedOnly && removePinnedOnlyActionFromOverflow(movedItem, overflowItems);

            // If it exists, remove it from our local collection of hidden overflow items,
            // add it to the start of the primary (surface) collection, compute the cacheKey, and return the new data.
            this.commandBarHiddenOverflowItems = this.commandBarHiddenOverflowItems.slice(1);
            primaryItems = [movedItem, ...primaryItems];
            cacheKey = this.computeCommandBarCacheKey(primaryItems);

            return { ...data, primaryItems, overflowItems, cacheKey };
        }

        // If we have no more hidden overflow items to add back to the primary (surface) collection, return undefined so the commandBar's resize logic will stop.
        return undefined;
    };

    private computeCommandBarCacheKey(primaryItems: SurfaceActionItem[]) {
        // Logic copied from fabric's command bar with some changes.
        const returnKey = (acc: string, current: SurfaceActionItem): string => {
            const { cacheKey = current.key } = current;
            return acc + cacheKey;
        };

        const primaryKey = primaryItems.reduce(returnKey, '');
        const farKey = ''; // We never have far items, always use an empty string.
        const overflowKey = 'overflow'; // We always have an overflow button, always use 'overflow'

        return [primaryKey, farKey, overflowKey].join(' ');
    }

    private onResize = () => {
        if (this.commandBar) {
            this.commandBar.remeasure();
        }
    };

    private tryCreateAddinsCommandBarItems(
        surfaceItems: SurfaceActionItem[],
        overflowItems: SurfaceActionItem[],
        action: ReadStaticAction
    ) {
        // Do not create addins command bar items if they have not finished initializing
        if (this.state.addinsInitializedState != AddinsInitializedState.Finished) {
            return;
        }

        const addinsSurfaceItems: SurfaceActionItem[] = [];
        const addinsOverflowItems: SurfaceActionItem[] = [];
        const surfaceActionsOptions = getOptionsForFeature<SurfaceActionsOptions>(
            OwsOptionsFeatureType.SurfaceActions
        );
        const datapointName = action.key;

        if (this.props.shouldIncludeFullInfo) {
            populateCommandBarWithAddins(
                ExtensibilityModeEnum.MessageRead,
                this.props.item,
                this.getFocusedItemId || this.props.item.ItemId.Id,
                surfaceActionsOptions.readSurfaceAddins,
                addinsSurfaceItems,
                addinsOverflowItems,
                true /* isEndAligned */,
                this.commandBarDividerKey,
                null /* className*/,
                true /* alwaysAddToOverflow */
            );
        }

        [...addinsSurfaceItems, ...addinsOverflowItems].forEach(item => {
            if (item.onClick) {
                const clickHandler = item.onClick;
                item.onClick = getHandlerAndLogAction(
                    clickHandler,
                    true /** isMouse */,
                    datapointName,
                    this.props.item,
                    this.props.viewState,
                    this.props.actionSource
                );
                item.onKeyDown = getHandlerAndLogAction(
                    clickHandler,
                    false /** isMouse */,
                    datapointName,
                    this.props.item,
                    this.props.viewState,
                    this.props.actionSource
                );
            }
        });

        surfaceItems.unshift(...addinsSurfaceItems);
        overflowItems.push(...addinsOverflowItems);
    }

    private shouldAddinsPopulate(): boolean {
        const shouldIncludeFullInfo =
            (!this.props.customizedSubsetActionKeys ||
                this.props.customizedSubsetActionKeys.length == 0) &&
            this.props.shouldIncludeFullInfo;
        const conversationId = getConversationId(this.props.viewState, this.props.item);
        return (
            isFeatureEnabled('rp-addins') &&
            shouldIncludeFullInfo &&
            !findInlineComposeViewState(conversationId) &&
            !this.props.isItemAttachment &&
            shouldAddinsActivate(this.props.item) &&
            !isGroupItem(this.props.item) &&
            !doesMessageHaveSmimePrefix(this.props.item)
        );
    }

    private initailizeAddinsIfNeeded() {
        if (
            this.state.addinsInitializedState == AddinsInitializedState.NotStarted &&
            this.shouldAddinsPopulate()
        ) {
            this.setState({ addinsInitializedState: AddinsInitializedState.Started });
            this.intializeAddinsTask = window.setTimeout(() => {
                this.initializeAddins();
            }, timeoutToInitializeAddins);
        }
    }

    private initializeAddins() {
        isFeatureEnabled('rp-addins') &&
            lazyCreateMailReadAdapter.import().then(createMailReadAdapter => {
                const adapter: MessageReadAdapter = createMailReadAdapter(
                    this.props.item,
                    this.props.viewState as ItemPartViewState
                );
                lazyInitializeAddinsForItem.import().then(initializeAddinsForItem => {
                    initializeAddinsForItem(
                        getReadHostItemIndex(this.props.item.ItemId.Id),
                        adapter
                    ).then(() => {
                        this.setState({ addinsInitializedState: AddinsInitializedState.Finished });
                    });
                });
            });
    }

    private deInitializeAddins() {
        clearTimeout(this.intializeAddinsTask);

        if (this.state.addinsInitializedState == AddinsInitializedState.NotStarted) {
            return;
        }
        isFeatureEnabled('rp-addins') &&
            lazyDeinitializeAddinsForItem.import().then(deinitializeAddinsForItem => {
                deinitializeAddinsForItem(getReadHostItemIndex(this.props.item.ItemId.Id));
            });
    }

    @computed
    private get getFocusedItemId(): string {
        if (this.props.viewState.isConversationItemPart) {
            // To ensure the addin associated with the correct "hostItemIndex",
            // we should use "focusedItemId" as the "hostItemIndex" to populate addin on all expanded item parts.
            const focusedItemPart = getFocusedItemPart();
            return focusedItemPart?.itemId;
        }
        return null;
    }
}
